/**
 * WCC360 — the learner's attempt history, from the source that always saves.
 *
 * Why this module exists
 * ----------------------
 * There are two records of "a test was taken":
 *
 *  1. `runtime_sessions` (+ `assessment_v2_responses`) — written by the v2
 *     walk itself. No trigger can refuse it, so it is always there.
 *  2. `assessments` — the older learner-facing row. Its insert passes through
 *     the cadence trigger (`enforce_assessment_cadence`), which legitimately
 *     rejects a second take inside the 90-day window and rejects any insert
 *     while an older row is still `in_progress`.
 *
 * Reading (2) alone means a completed run can be fully saved and still look
 * like it never happened. So history reads BOTH and merges: the sealed
 * session is the floor, the `assessments` row is an enrichment. A blocked
 * `assessments` insert can never again hide a completed run.
 */
import { supabase } from "@/integrations/supabase/client";

export type AttemptSource = "assessment" | "session";

export interface Attempt {
  /** Row id — the `assessments` id when we have one, else the session id. */
  id: string;
  status: string;
  age_group: string | null;
  started_at: string | null;
  created_at: string | null;
  /** Where the row came from. Diagnostics only; never shown as jargon. */
  source: AttemptSource;
  /** The runtime session behind the attempt, when known. */
  session_id: string | null;
  /** Answers recorded for this attempt, when known. */
  answered: number | null;
}

const BANDS = new Set(["12-14", "15-18", "18+"]);

function bandOf(metadata: unknown): string | null {
  const meta = (metadata ?? {}) as Record<string, unknown>;
  const band = typeof meta.band === "string" ? meta.band : null;
  return band && BANDS.has(band) ? band : null;
}

function isV2(metadata: unknown): boolean {
  const meta = (metadata ?? {}) as Record<string, unknown>;
  // Runs written by automated checks are not the learner's attempts, and must
  // never appear in history or count against the take cadence.
  if (meta.seeded_by) return false;
  return meta.source === "assessment-v2";
}

/** Session status → the vocabulary the history list already speaks. */
function statusOf(row: { status: string | null; completed_at: string | null }): string {
  if (row.status === "completed" || row.completed_at) return "completed";
  if (row.status === "abandoned" || row.status === "expired") return "abandoned";
  return "in_progress";
}

export interface MergeInput {
  assessments: Array<{
    id: string;
    status: string;
    age_group: string | null;
    started_at: string | null;
    created_at: string | null;
  }>;
  sessions: Array<{
    id: string;
    status: string | null;
    started_at: string | null;
    completed_at: string | null;
    items_responded: number | null;
    metadata: unknown;
  }>;
}

/** Minutes inside which an `assessments` row and a session are the same run. */
const SAME_RUN_MS = 6 * 60 * 60 * 1000;

/**
 * Merge the two records into one list, newest first.
 *
 * Pairing rule: a session and an `assessments` row are the same attempt when
 * their bands agree and they started within six hours of each other. Pairing
 * keeps the `assessments` id (so existing links keep working) and takes the
 * more advanced of the two statuses — a completed session with a stalled
 * `assessments` row is a completed attempt.
 */
export function mergeAttempts(input: MergeInput): Attempt[] {
  const sessions = input.sessions.filter((s) => isV2(s.metadata));
  const usedSession = new Set<string>();
  const out: Attempt[] = [];

  for (const a of input.assessments) {
    if (a.status === "archived") continue;
    const at = new Date(a.started_at ?? a.created_at ?? 0).getTime();
    const match = sessions.find((s) => {
      if (usedSession.has(s.id)) return false;
      const band = bandOf(s.metadata);
      if (band && a.age_group && band !== a.age_group) return false;
      const st = new Date(s.started_at ?? 0).getTime();
      return Math.abs(st - at) <= SAME_RUN_MS;
    });
    if (match) usedSession.add(match.id);
    const sessionStatus = match ? statusOf(match) : null;
    out.push({
      id: a.id,
      status:
        a.status === "scored"
          ? "scored"
          : sessionStatus === "completed" || a.status === "completed"
            ? "completed"
            : a.status,
      age_group: a.age_group ?? (match ? bandOf(match.metadata) : null),
      started_at: a.started_at ?? a.created_at,
      created_at: a.created_at,
      source: "assessment",
      session_id: match?.id ?? null,
      answered: match?.items_responded ?? null,
    });
  }

  for (const s of sessions) {
    if (usedSession.has(s.id)) continue;
    const status = statusOf(s);
    // An abandoned session with nothing recorded isn't an attempt worth showing.
    if (status !== "completed" && !(s.items_responded ?? 0)) continue;
    out.push({
      id: s.id,
      status,
      age_group: bandOf(s.metadata),
      started_at: s.started_at,
      created_at: s.started_at,
      source: "session",
      session_id: s.id,
      answered: s.items_responded ?? null,
    });
  }

  return out.sort(
    (a, b) =>
      new Date(b.started_at ?? b.created_at ?? 0).getTime() -
      new Date(a.started_at ?? a.created_at ?? 0).getTime(),
  );
}

/** Every attempt this learner has made, from both records. */
export async function fetchAttempts(userId: string, limit = 10): Promise<Attempt[]> {
  const [assessmentsRes, sessionsRes] = await Promise.all([
    supabase
      .from("assessments")
      .select("id, status, age_group, started_at, created_at")
      .eq("user_id", userId)
      .neq("status", "archived")
      .order("created_at", { ascending: false })
      .limit(limit * 2),
    supabase
      .from("runtime_sessions")
      .select("id, status, started_at, completed_at, items_responded, metadata")
      .eq("learner_id", userId)
      .order("started_at", { ascending: false })
      .limit(limit * 4),
  ]);

  if (assessmentsRes.error) {
    // eslint-disable-next-line no-console
    console.error("[wcc360.attempts] assessments read failed:", assessmentsRes.error.message);
  }
  if (sessionsRes.error) {
    // eslint-disable-next-line no-console
    console.error("[wcc360.attempts] sessions read failed:", sessionsRes.error.message);
  }

  return mergeAttempts({
    assessments: assessmentsRes.data ?? [],
    sessions: sessionsRes.data ?? [],
  }).slice(0, limit);
}
